import { QuickPassUser } from '../_types/quickpass-user'
import {
  QuickPassProfile,
  QuickPassUnitPreferences,
} from '../_types/quickpass-profile'
import { defaultQuickPassProfile } from '../_defaults/quickpass-profile'
import { ImportedListing } from '../_types/imported-listing'
import { QuickPassNotification } from '../_types/quickpass-notification'

const USER_STORAGE_KEY_PREFIX = 'quickpass:dev:user:'
const PROFILE_STORAGE_KEY_PREFIX = 'quickpass:dev:profile:'
const IMPORTED_LISTINGS_STORAGE_KEY_PREFIX = 'quickpass:dev:imported-listings:'
const NOTIFICATIONS_STORAGE_KEY_PREFIX = 'quickpass:dev:notifications:'

/**
 * In-browser fallback store used when no real backend is configured. Lets
 * the full renter UX (sign-in → onboarding → dashboard → profile) work
 * without a deployed api-server-gateway. When `VITE_API_URL` is set to a
 * reachable gateway, the data-layer functions short-circuit this store
 * entirely.
 */

const readUser = (firebaseAuthUid: string): QuickPassUser | null => {
  try {
    const raw = window.localStorage.getItem(
      `${USER_STORAGE_KEY_PREFIX}${firebaseAuthUid}`,
    )

    if (raw === null) {
      return null
    }

    return JSON.parse(raw) as QuickPassUser
  } catch (error) {
    console.error('local-store: failed to read user', error)
    return null
  }
}

const writeUser = (user: QuickPassUser): void => {
  try {
    window.localStorage.setItem(
      `${USER_STORAGE_KEY_PREFIX}${user.firebaseAuthUid}`,
      JSON.stringify(user),
    )
  } catch (error) {
    console.error('local-store: failed to write user', error)
  }
}

const readProfile = (quickPassUserId: string): QuickPassProfile | null => {
  try {
    const raw = window.localStorage.getItem(
      `${PROFILE_STORAGE_KEY_PREFIX}${quickPassUserId}`,
    )

    if (raw === null) {
      return null
    }

    return JSON.parse(raw) as QuickPassProfile
  } catch (error) {
    console.error('local-store: failed to read profile', error)
    return null
  }
}

const writeProfile = (profile: QuickPassProfile): void => {
  try {
    window.localStorage.setItem(
      `${PROFILE_STORAGE_KEY_PREFIX}${profile.quickPassUserId}`,
      JSON.stringify(profile),
    )
  } catch (error) {
    console.error('local-store: failed to write profile', error)
  }
}

/**
 * Builds a stable QuickPassUser shim from a Firebase user identity. Used
 * when the backend is unreachable — id derives from firebaseAuthUid so it
 * stays consistent across page loads and sessions.
 *
 * @param {string} firebaseAuthUid
 * @param {Partial<QuickPassUser>} input
 * @returns {QuickPassUser}
 */
const buildSyntheticUser = (
  firebaseAuthUid: string,
  input: Partial<QuickPassUser>,
): QuickPassUser => {
  const now = Date.now()
  const stableId = `qp_user_${firebaseAuthUid}`

  return {
    id: stableId,
    firebaseAuthUid,
    quickCasaUserId: '',
    email: input.email ?? '',
    phone: input.phone ?? null,
    displayName: input.displayName ?? null,
    authProviders: input.authProviders ?? [],
    pushTokens: input.pushTokens ?? [],
    createdAt: now,
    updatedAt: now,
    lastSignInAt: now,
  }
}

/**
 * Merges a partial profile patch onto the previous profile (or builds a
 * fresh one with defaults), stamping timestamps appropriately.
 *
 * @param {string} quickPassUserId
 * @param {Partial<QuickPassProfile>} patch
 * @param {QuickPassProfile | null} previousProfile
 * @returns {QuickPassProfile}
 */
const mergeProfilePatch = (
  quickPassUserId: string,
  patch: Partial<QuickPassProfile>,
  previousProfile: QuickPassProfile | null,
): QuickPassProfile => {
  const now = Date.now()

  if (previousProfile === null) {
    const sanitisedPatch: Partial<QuickPassProfile> = { ...patch }
    delete sanitisedPatch.id
    delete sanitisedPatch.quickPassUserId
    delete sanitisedPatch.createdAt

    return {
      ...defaultQuickPassProfile,
      ...sanitisedPatch,
      id: quickPassUserId,
      quickPassUserId,
      createdAt: now,
      updatedAt: now,
    }
  }

  const sanitisedPatch: Partial<QuickPassProfile> = { ...patch }
  delete sanitisedPatch.id
  delete sanitisedPatch.quickPassUserId
  delete sanitisedPatch.createdAt

  const mergedPreferences: QuickPassUnitPreferences =
    sanitisedPatch.unitPreferences ?? previousProfile.unitPreferences

  return {
    ...previousProfile,
    ...sanitisedPatch,
    unitPreferences: mergedPreferences,
    updatedAt: now,
  }
}

const readImportedListings = (quickPassUserId: string): ImportedListing[] => {
  try {
    const raw = window.localStorage.getItem(
      `${IMPORTED_LISTINGS_STORAGE_KEY_PREFIX}${quickPassUserId}`,
    )

    if (raw === null) {
      return []
    }

    const parsed = JSON.parse(raw) as ImportedListing[]
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    console.error('local-store: failed to read imported listings', error)
    return []
  }
}

const writeImportedListings = (
  quickPassUserId: string,
  listings: ImportedListing[],
): void => {
  try {
    window.localStorage.setItem(
      `${IMPORTED_LISTINGS_STORAGE_KEY_PREFIX}${quickPassUserId}`,
      JSON.stringify(listings),
    )
  } catch (error) {
    console.error('local-store: failed to write imported listings', error)
  }
}

const readNotifications = (
  quickPassUserId: string,
): QuickPassNotification[] => {
  try {
    const raw = window.localStorage.getItem(
      `${NOTIFICATIONS_STORAGE_KEY_PREFIX}${quickPassUserId}`,
    )

    if (raw === null) {
      return []
    }

    const parsed = JSON.parse(raw) as QuickPassNotification[]
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    console.error('local-store: failed to read notifications', error)
    return []
  }
}

const writeNotifications = (
  quickPassUserId: string,
  notifications: QuickPassNotification[],
): void => {
  try {
    window.localStorage.setItem(
      `${NOTIFICATIONS_STORAGE_KEY_PREFIX}${quickPassUserId}`,
      JSON.stringify(notifications),
    )
  } catch (error) {
    console.error('local-store: failed to write notifications', error)
  }
}

/**
 * Wipes any locally cached imported listings + notifications for the
 * supplied renter. Real Firebase Auth users should never see seed/dev data
 * — call this on real sign-in to evict anything a previous broken run
 * might have left behind.
 *
 * @param {string} quickPassUserId - the renter to clear
 * @returns {void}
 */
const clearImportedListingsAndNotifications = (
  quickPassUserId: string,
): void => {
  try {
    window.localStorage.removeItem(
      `${IMPORTED_LISTINGS_STORAGE_KEY_PREFIX}${quickPassUserId}`,
    )
    window.localStorage.removeItem(
      `${NOTIFICATIONS_STORAGE_KEY_PREFIX}${quickPassUserId}`,
    )
  } catch (error) {
    console.error(
      'local-store: failed to clear cached listings + notifications',
      error,
    )
  }
}

const ALL_DEV_KEY_PREFIXES = [
  USER_STORAGE_KEY_PREFIX,
  PROFILE_STORAGE_KEY_PREFIX,
  IMPORTED_LISTINGS_STORAGE_KEY_PREFIX,
  NOTIFICATIONS_STORAGE_KEY_PREFIX,
]

/**
 * Wipes every `quickpass:dev:*` localStorage entry. Use when transitioning
 * to a real Firebase Auth session — these keys exist for the dev-bypass
 * mode and any value sitting under them for a real-auth user is stale
 * carry-over from a previous broken run.
 *
 * @returns {void}
 */
const clearAllDevLocalStore = (): void => {
  try {
    const keysToRemove: string[] = []
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index)
      if (key === null) {
        continue
      }
      const isDevKey = ALL_DEV_KEY_PREFIXES.some(prefix =>
        key.startsWith(prefix),
      )
      if (isDevKey === true) {
        keysToRemove.push(key)
      }
    }
    for (const key of keysToRemove) {
      window.localStorage.removeItem(key)
    }
  } catch (error) {
    console.error('local-store: failed to clear dev keys', error)
  }
}

export {
  readUser,
  writeUser,
  readProfile,
  writeProfile,
  buildSyntheticUser,
  mergeProfilePatch,
  readImportedListings,
  writeImportedListings,
  readNotifications,
  writeNotifications,
  clearImportedListingsAndNotifications,
  clearAllDevLocalStore,
}
